import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Leaf, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppDispatch } from "@/redux/hooks";
import { setCredentials } from "@/redux/slices/authSlice";
import { useRegisterOwnerMutation } from "@/redux/slices/authApi";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

const schema = z.object({
  name: z.string().min(2, "Name is required").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  password: z.string().min(6, "Min 6 characters").max(72),
  className: z.string().min(2, "Class/Institute name is required").max(100),
  subdomain: z.string().min(3, "Subdomain is required").max(50).regex(/^[a-z0-9-]+$/, "Only lowercase letters, numbers, and hyphens"),
});
type FormValues = z.infer<typeof schema>;

export default function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", password: "", className: "", subdomain: "" },
  });

  const [registerOwner, { isLoading }] = useRegisterOwnerMutation();

  const onSubmit = async (values: FormValues) => {
    try {
      const response = await registerOwner(values).unwrap();
      dispatch(
        setCredentials({
          user: response.user,
          token: response.token,
          tenant: response.tenant,
        })
      );
      toast.success("Registration successful");
      navigate("/");
    } catch (error: any) {
      toast.error(error.data?.message || "Registration failed");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-soft via-background to-background p-4">
      <div className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-floating sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Leaf className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Ayursangrah</h1>
            <p className="text-sm text-muted-foreground">Register your institute</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Your Name</Label>
            <Input id="name" type="text" {...register("name")} placeholder="John Doe" />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="className">Institute Name</Label>
            <Input id="className" type="text" {...register("className")} placeholder="Ayursangrah Academy" />
            {errors.className && <p className="text-xs text-destructive">{errors.className.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="subdomain">Subdomain</Label>
            <Input id="subdomain" type="text" {...register("subdomain")} placeholder="ayursangrah" />
            {errors.subdomain && <p className="text-xs text-destructive">{errors.subdomain.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...register("email")} placeholder="admin@example.com" />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input 
                id="password" 
                type={showPassword ? "text" : "password"} 
                {...register("password")} 
                placeholder="••••••••" 
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting || isLoading}>
            {isSubmitting || isLoading ? "Registering..." : "Register"}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4">
            Already have an account? <Link to="/login" className="text-primary underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
